import { useSelector } from 'react-redux'
import { useCallback, useState } from 'react'
import { Box } from '@mui/material'
import { FlexColumn, FlexRow } from '../../atoms/layout/Flex'
import SearchField from '../../molecules/inputs/SearchField'
import FormItem from '../../molecules/inputs/FormItem'
import RadioGroup from '../../atoms/inputs/RadioGroup'
import Button from '../../atoms/inputs/Button'
import { JobSelectors } from '../../../store/job/selectors'
import { ConstructionType, JobArea } from '../../../types/job'
import ClientSearchAreaSelectDialog from './ClientSearchAreaSelectDialog'
import CompanyWorkAreasView from './CompanyWorkAreasView'
import ClientSearchConstructionSelectDialog from './ClientSearchConstructionSelectDialog'
import Label from '../../atoms/display/Label'

type Props = {
  keyword?: string | null
  licenseNumber: number
  evaluation: number
  areas: string[]
  constructions: string[]
  onChange: (
    keyword: string | null,
    licenseNumber: number,
    evaluation: number,
    areas: string[],
    constructions: string[]
  ) => void
}

export default function ClientSearchForm({
  keyword,
  licenseNumber,
  evaluation,
  areas,
  constructions,
  onChange,
}: Props) {
  const [areaSelectDialog, setAreaSelectDialog] = useState(false)
  const [constructionSelectDialog, setConstructionSelectDialog] = useState(false)
  const jobAreas = useSelector(JobSelectors.areas)
  const constructionTypes = useSelector(JobSelectors.constructionTypes)
  const jobAreaMap: { [key: string]: JobArea } = {}
  const constructionTypeMap: { [key: string]: ConstructionType } = {}
  jobAreas.forEach((area) => {
    jobAreaMap[area.id] = area
    area.children.forEach((childArea) => {
      jobAreaMap[childArea.id] = childArea
    })
  })
  constructionTypes.forEach((type) => {
    constructionTypeMap[type.id] = type
  })

  const handleChangeKeyword = useCallback(
    (value?: string) => {
      onChange(value ?? null, licenseNumber, evaluation, areas, constructions)
    },
    [areas, constructions, evaluation, licenseNumber, onChange]
  )

  const handleChangeLicenseNumber = useCallback(
    (value: string | number | boolean) => {
      if (typeof value === 'number') {
        onChange(keyword ?? null, value, evaluation, areas, constructions)
      }
      if (typeof value === 'string') {
        onChange(keyword ?? null, Number(value), evaluation, areas, constructions)
      }
    },
    [areas, constructions, evaluation, keyword, onChange]
  )

  const handleChangeAreas = useCallback(
    (ids: string[]) => {
      onChange(keyword ?? null, licenseNumber, evaluation, ids, constructions)
    },
    [constructions, evaluation, keyword, licenseNumber, onChange]
  )

  const handleChangeConstructions = useCallback(
    (ids: string[]) => {
      onChange(keyword ?? null, licenseNumber, evaluation, areas, ids)
    },
    [areas, evaluation, keyword, licenseNumber, onChange]
  )

  return (
    <FlexColumn>
      <SearchField value={keyword ?? undefined} onChange={handleChangeKeyword} />
      <FormItem label="事業者登録番号の有無">
        <RadioGroup
          value={licenseNumber}
          items={[
            { value: 2, label: '全て' },
            { value: 1, label: 'あり' },
            { value: 0, label: 'なし' },
          ]}
          row
          onChange={handleChangeLicenseNumber}
        />
      </FormItem>
      <FormItem label="対応可能地域">
        <FlexRow align="center">
          <FlexRow width={80}>
            <Button size="sm" height="small" variant="outlined" onClick={() => setAreaSelectDialog(true)}>
              選択
            </Button>
          </FlexRow>
          <CompanyWorkAreasView areaIds={areas} highlightAreaIds={[]} />
          <ClientSearchAreaSelectDialog
            open={areaSelectDialog}
            onChange={handleChangeAreas}
            onClose={() => setAreaSelectDialog(false)}
          />
        </FlexRow>
      </FormItem>
      <FormItem label="対応可能工種">
        <FlexRow align="center">
          <FlexRow width={80}>
            <Button size="sm" height="small" variant="outlined" onClick={() => setConstructionSelectDialog(true)}>
              選択
            </Button>
          </FlexRow>
          <FlexRow wrap>
            {constructions.length === 0 && (
              <Box px={0.5}>
                <Label text="未設定" />
              </Box>
            )}
            {constructions.map((construction) => (
              <Label key={construction} text={constructionTypeMap[construction]?.name} />
            ))}
          </FlexRow>
        </FlexRow>
        <ClientSearchConstructionSelectDialog
          open={constructionSelectDialog}
          onChange={handleChangeConstructions}
          onClose={() => setConstructionSelectDialog(false)}
        />
      </FormItem>
    </FlexColumn>
  )
}
